import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

// import {
//   clampMiddleCarouselPageIndex,
//   getMiddleCarouselBudget,
//   getMiddleCarouselNeedsNav,
//   getMiddleCarouselPageCount,
//   getMiddleCarouselPageSlice,
// } from '../glassWidgetCarouselMath';
import glassWidgetCarouselMath from '../glassWidgetCarouselHelpers';

const MIDDLE_CAROUSEL_AUTO_ADVANCE_MS = 12_000;

/**
 * @param root0
 * @param root0.maxVisible
 * @param root0.pinnedTop
 * @param root0.pinnedBottom
 * @param root0.middleCards
 */
const GlassWidgetCardCarousel = ({ maxVisible, pinnedTop, pinnedBottom, middleCards }) => {
  const [middlePage, setMiddlePage] = useState(0);
  const [middleCarouselPaused, setMiddleCarouselPaused] = useState(false);

  const pinnedSlots = pinnedTop.length + pinnedBottom.length;
  const middleBudget = useMemo(
    () => glassWidgetCarouselMath.getMiddleCarouselBudget(maxVisible, pinnedSlots),
    [maxVisible, pinnedSlots],
  );
  const pageCount = useMemo(
    () => glassWidgetCarouselMath.getMiddleCarouselPageCount(middleCards.length, middleBudget),
    [middleCards.length, middleBudget],
  );

  const needsMiddleNav = useMemo(
    () => glassWidgetCarouselMath.getMiddleCarouselNeedsNav(maxVisible, pinnedSlots, middleCards.length),
    [maxVisible, pinnedSlots, middleCards.length],
  );

  const effectiveMiddlePage = useMemo(
    () => glassWidgetCarouselMath.clampMiddleCarouselPageIndex(middlePage, pageCount),
    [middlePage, pageCount],
  );

  const visibleMiddle = useMemo(
    () => glassWidgetCarouselMath.getMiddleCarouselPageSlice(middleCards, effectiveMiddlePage, middleBudget),
    [middleCards, effectiveMiddlePage, middleBudget],
  );

  const goPrev = useCallback(() => {
    setMiddlePage((p) => {
      const cur = glassWidgetCarouselMath.clampMiddleCarouselPageIndex(p, pageCount);
      return cur <= 0 ? pageCount - 1 : cur - 1;
    });
  }, [pageCount]);

  const goNext = useCallback(() => {
    setMiddlePage((p) => {
      const cur = glassWidgetCarouselMath.clampMiddleCarouselPageIndex(p, pageCount);
      return cur >= pageCount - 1 ? 0 : cur + 1;
    });
  }, [pageCount]);

  useEffect(() => {
    if (!needsMiddleNav || middleCarouselPaused) return undefined;
    const id = window.setInterval(() => {
      goNext();
    }, MIDDLE_CAROUSEL_AUTO_ADVANCE_MS);
    return () => window.clearInterval(id);
  }, [needsMiddleNav, middleCarouselPaused, goNext]);

  const prevAria = 'Previous widget cards';
  const nextAria = 'Next widget cards';
  const middleRegionAria = 'Additional home widgets';

  const middlePageStatusText = useMemo(
    () => `Page ${effectiveMiddlePage + 1} of ${pageCount}`,
    [effectiveMiddlePage, pageCount],
  );

  return (
    <React.Fragment>
      {pinnedTop}
      <div
        className="glass-widget-card-carousel__middle"
        role={needsMiddleNav ? 'region' : undefined}
        aria-label={needsMiddleNav ? middleRegionAria : undefined}
        onMouseEnter={() => setMiddleCarouselPaused(true)}
        onMouseLeave={() => setMiddleCarouselPaused(false)}
      >
        <div className="glass-widget-card-carousel__middle-stack">
          {needsMiddleNav ? (
            <span className="glass-widget-card-carousel__sr-only" aria-live="polite" aria-atomic="true">
              {middlePageStatusText}
            </span>
          ) : null}
          {visibleMiddle}
          {needsMiddleNav ? (
            <React.Fragment>
              <button
                className="glass-widget-card-carousel__nav-btn glass-widget-card-carousel__nav-btn--prev"
                type="button"
                onClick={goPrev}
                aria-label={prevAria}
              >
                <FontAwesomeIcon icon={['fas', 'angle-left']} />
              </button>
              <button
                className="glass-widget-card-carousel__nav-btn glass-widget-card-carousel__nav-btn--next"
                type="button"
                onClick={goNext}
                aria-label={nextAria}
              >
                <FontAwesomeIcon icon={['fas', 'angle-right']} />
              </button>
            </React.Fragment>
          ) : null}
        </div>
      </div>
      {pinnedBottom}
    </React.Fragment>
  );
};

export default GlassWidgetCardCarousel;
